import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    CheckCircle,
    Users,
    BookOpen,
    Award,
    FileText,
    Send,
    Check,
    CreditCard,
    UserCircle,
    Briefcase,
} from 'lucide-react';
import wilayasService, { WilayaDto } from '../../services/wilayas.service';
import adhesionsService from '../../services/adhesions.service';

import bulletinPdf from '../../assets/BA-SAPI.pdf';

interface FormData {
    civilite: string;
    nom: string;
    prenom: string;
    dateNaissance: string;
    lieuNaissance: string;
    email: string;
    adressePersonnelle: string;
    telephone: string;
    fonctionTitre: string;
    etablissementStructure: string;
    secteur: string;
    autreSecteur: string;
    adresse: string;
    wilaya: string;
}

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1
        }
    }
};

const Adhesion: React.FC = () => {
    const navigate = useNavigate();
    const [isExistingMember, setIsExistingMember] = useState<boolean | null>(null);
    const [wilayas, setWilayas] = useState<WilayaDto[]>([]);
    const [formData, setFormData] = useState<FormData>({
        civilite: '',
        nom: '',
        prenom: '',
        dateNaissance: '',
        lieuNaissance: '',
        email: '',
        adressePersonnelle: '',
        telephone: '',
        fonctionTitre: '',
        etablissementStructure: '',
        secteur: '',
        autreSecteur: '',
        adresse: '',
        wilaya: '',
    });
    const [errors, setErrors] = useState<Partial<Record<keyof FormData | '_form', string>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        wilayasService.findAll().then(setWilayas);
    }, []);

    const handleMemberStatusChange = (status: boolean) => {
        setIsExistingMember(status);
        if (status) {
            navigate('/adherent-login');
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        if (errors[name as keyof FormData] || (errors as any)['_form']) {
            setErrors((prev) => ({ ...prev, [name]: undefined, _form: undefined }));
        }
    };

    // Removed handleFileChange

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (!formData.civilite) newErrors.civilite = 'La civilité est requise';
        if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
        if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
        if (!formData.dateNaissance) {
            newErrors.dateNaissance = 'La date de naissance est requise';
        } else {
            // Must be at least 18 years old
            const dob = new Date(formData.dateNaissance);
            const today = new Date();
            const age = today.getFullYear() - dob.getFullYear() -
                (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
            if (age < 18) {
                newErrors.dateNaissance = 'Vous devez avoir au moins 18 ans pour adhérer';
            }
        }
        if (!formData.lieuNaissance.trim()) newErrors.lieuNaissance = 'Le lieu de naissance est requis';
        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|dz|net|org|fr|info|edu|gov|biz|name|[A-Z]{2})$/i.test(formData.email)) {
            newErrors.email = 'Email invalide';
        }
        if (!formData.adressePersonnelle.trim()) newErrors.adressePersonnelle = 'L\'adresse personnelle est requise';
        if (!formData.telephone.trim()) {
            newErrors.telephone = 'Le téléphone est requis';
        } else if (!/^[0-9]{10}$/.test(formData.telephone.replace(/\s/g, ''))) {
            newErrors.telephone = 'Numéro de téléphone invalide (10 chiffres)';
        }
        if (!formData.fonctionTitre.trim()) newErrors.fonctionTitre = 'La fonction / titre est requise';
        if (!formData.etablissementStructure.trim()) newErrors.etablissementStructure = 'L\'établissement ou structure est requis';
        if (!formData.secteur) newErrors.secteur = 'Le secteur est requis';
        if (formData.secteur === 'Autres' && !formData.autreSecteur.trim()) {
            newErrors.autreSecteur = 'Veuillez préciser le secteur';
        }
        if (!formData.adresse.trim()) newErrors.adresse = 'L\'adresse d\'exercice est requise';
        if (!formData.wilaya) newErrors.wilaya = 'La wilaya est requise';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            const submissionData = {
                civilite: formData.civilite,
                nom: formData.nom,
                prenom: formData.prenom,
                dateNaissance: formData.dateNaissance,
                lieuNaissance: formData.lieuNaissance,
                email: formData.email,
                adressePersonnelle: formData.adressePersonnelle,
                telephone: formData.telephone,
                fonctionTitre: formData.fonctionTitre,
                etablissementStructure: formData.etablissementStructure,
                secteur: formData.secteur === 'Autres' ? formData.autreSecteur : formData.secteur,
                adresse: formData.adresse,
                wilaya: formData.wilaya,
                accepteAdhesion: true
            };

            await adhesionsService.create(submissionData);
            alert('Votre demande d\'adhésion a été soumise avec succès. Vous recevrez un email de confirmation.');

            setFormData({
                civilite: '',
                nom: '',
                prenom: '',
                dateNaissance: '',
                lieuNaissance: '',
                email: '',
                adressePersonnelle: '',
                telephone: '',
                fonctionTitre: '',
                etablissementStructure: '',
                secteur: '',
                autreSecteur: '',
                adresse: '',
                wilaya: '',
            });
            setIsExistingMember(null);
        } catch (error: any) {
            console.error('Error submitting form:', error);
            const message: string = error.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.';

            // Map known server-side errors to specific fields
            if (message.toLowerCase().includes('18 ans')) {
                setErrors((prev) => ({ ...prev, dateNaissance: message }));
            } else if (message.toLowerCase().includes('email') && message.toLowerCase().includes('existe')) {
                setErrors((prev) => ({ ...prev, email: 'Une demande existe déjà avec cet email' }));
            } else {
                // Generic form-level error displayed below the form
                setErrors((prev) => ({ ...prev, _form: message } as any));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white selection:bg-primary/20 selection:text-primary-dark font-sans">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] opacity-60 translate-x-1/3 -translate-y-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[80px] opacity-40 -translate-x-1/3 translate-y-1/3"></div>

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="inline-block px-4 py-1.5 rounded-full bg-primary/5 text-primary text-sm font-semibold mb-6 tracking-wide uppercase"
                    >
                        Notre Organisation
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tight text-gray-900"
                    >
                        Adhésion à <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">la SAPI</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="text-xl md:text-2xl font-light text-gray-500 max-w-4xl mx-auto leading-relaxed"
                    >
                        Rejoindre la Société Algérienne de Pharmacie Industrielle (SAPI), c’est intégrer une communauté d’experts, chercheurs et industriels unis par une même ambition : construire une industrie pharmaceutique algérienne innovante, compétitive et durable.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

                {/* Pourquoi adhérer */}
                <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={staggerContainer}
                    className="mb-20"
                >
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Pourquoi adhérer ?</h2>
                        <div className="w-20 h-1 bg-primary/20 mx-auto rounded-full"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                icon: <Users className="w-8 h-8" />,
                                text: "Participer à des événements scientifiques et professionnels : conférences, séminaires, formations et ateliers."
                            },
                            {
                                icon: <Users className="w-8 h-8" />,
                                text: "Accéder à un réseau national et international de spécialistes du domaine pharmaceutique."
                            },
                            {
                                icon: <BookOpen className="w-8 h-8" />,
                                text: "Bénéficier de ressources exclusives (publications, études, appels à projets, offres de collaboration)."
                            },
                            {
                                icon: <Award className="w-8 h-8" />,
                                text: "Contribuer activement à la promotion de la recherche, de la qualité et de l’innovation dans le secteur pharmaceutique algérien."
                            },
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                variants={fadeInUp}
                                className="bg-white p-6 rounded-2xl shadow-sm border border-primary/10 hover:shadow-xl transition-all duration-300 group"
                            >
                                <div className="w-14 h-14 bg-primary/5 rounded-xl flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                                    {item.icon}
                                </div>
                                <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                <div className="grid lg:grid-cols-2 gap-12 mb-20">
                    {/* Conditions */}
                    <motion.section
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <CheckCircle className="w-6 h-6 text-primary mr-3" />
                            Conditions d’adhésion
                        </h2>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-primary/10">
                            <p className="text-gray-700 mb-6">
                                Pour devenir membre de la SAPI, le candidat doit remplir les conditions suivantes :
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "Être de nationalité algérienne ",
                                    "Être titulaire d’un diplôme universitaire en lien avec la pharmacie industrielle (pharmacie, chimie, biotechnologie, sciences du médicament, etc.) ",
                                    "Être actif dans la recherche, l’enseignement ou l’industrie pharmaceutique ",
                                    "S’acquitter des cotisations annuelles prévues par la SAPI."
                                ].map((condition, idx) => (
                                    <li key={idx} className="flex items-start space-x-3">
                                        <div className="mt-1 flex-shrink-0 w-5 h-5 bg-secondary/10 rounded-full flex items-center justify-center">
                                            <Check className="w-3 h-3 text-secondary" />
                                        </div>
                                        <span className="text-gray-700">{condition}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.section>

                    {/* Procédure */}
                    <motion.section
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <CreditCard className="w-6 h-6 text-primary mr-3" />
                            Procédure d’adhésion
                        </h2>
                        <div className="bg-white p-8 rounded-2xl border border-primary/10 shadow-sm">
                            <p className="text-gray-700 mb-6">
                                L’adhésion à la SAPI se fait exclusivement en ligne :
                            </p>
                            <ol className="space-y-6">
                                <li className="flex gap-4">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>
                                    <div className="text-gray-600 text-sm pt-1 space-y-2">
                                        <p>Renseigner le formulaire d’inscription ci-dessous.</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>
                                    <p className="text-gray-600 text-sm pt-1">
                                        Lorsque votre dossier sera validé par la SAPI, vous recevrez un avis d’acceptation de votre adhésion par courriel à l’adresse e-mail que vous avez insérée dans le formulaire d’adhésion.
                                    </p>
                                </li>
                                <li className="flex gap-4">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">3</span>
                                    <div className="text-gray-600 text-sm pt-1 space-y-3">
                                        <p>Vous devrez alors vous acquitter des frais d’adhésion annuelle fixés à <span className="font-bold text-primary">2000 DA / an</span> par virement bancaire au compte ouvert :</p>
                                        <div className="p-4 bg-primary/5 rounded-xl border border-primary/5">
                                            <p className="font-semibold text-gray-900">Société Algérienne de Pharmacie Industrielle</p>
                                            <p className="font-mono text-primary font-bold mt-1">Cpt n° : 00400117410023079014</p>
                                            <p className="text-xs text-gray-500 mt-1">Crédit Populaire d’Algérie, agence Emir El Khettabi<br />01, Rue Emir El-Khettabi, 16000, Alger</p>
                                        </div>
                                        <p>et transmettre l’avis de virement par email à l’adresse : <a href="mailto:adhesion@sapi-dz.org" className="text-primary hover:underline">adhesion@sapi-dz.org</a></p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">4</span>
                                    <p className="text-gray-600 text-sm pt-1">
                                        Dès réception de l’avis de paiement, vous recevrez votre numéro de membre SAPI ainsi que vos accès à l’espace adhérent. Il vous sera demandé une photo, une attestation de travail de moins de 30 jours remise par votre employeur, aux fins de l’établissement de votre carte de membre.
                                    </p>
                                </li>
                            </ol>
                        </div>
                    </motion.section>
                </div>

                {/* Renouvellement */}
                <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    className="mb-20 bg-white p-8 rounded-2xl shadow-sm border border-primary/10"
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <Award className="w-6 h-6 text-primary mr-3" />
                        Renouvellement de l’adhésion
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        L’adhésion est valable pour une durée d’un (1) an et doit être renouvelée avant le 31 janvier de chaque année pour conserver tous les avantages liés au statut de membre.
                    </p>
                </motion.section>

                {/* Formulaire */}
                <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInUp}
                    className="max-w-4xl mx-auto"
                >
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-primary/10">
                        <div className="bg-gradient-to-r from-primary to-primary-dark p-8 text-white text-center">
                            <h2 className="text-3xl font-bold mb-2">Formulaire d'adhésion</h2>
                            <p className="text-white/80">Rejoignez-nous en quelques clics</p>
                        </div>

                        <div className="p-8 md:p-12">
                            {/* Member Status Switch */}
                            <div className="flex justify-center mb-10">
                                <div className="bg-white p-1.5 rounded-xl inline-flex border border-gray-100 shadow-sm">
                                    <button
                                        onClick={() => handleMemberStatusChange(false)}
                                        className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isExistingMember === false
                                            ? 'bg-primary text-white shadow-md'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        Nouvelle adhésion
                                    </button>
                                    <button
                                        onClick={() => handleMemberStatusChange(true)}
                                        className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isExistingMember === true
                                            ? 'bg-primary text-white shadow-md'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        Déjà membre ?
                                    </button>
                                </div>
                            </div>

                            {isExistingMember === false && (
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    {/* Personal Info */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                                            <UserCircle className="w-5 h-5 mr-2 text-primary" />
                                            Informations Personnelles
                                        </h3>
                                        <div className="mb-6">
                                            <label className="text-sm font-medium text-gray-700 block mb-3">Civilité</label>
                                            <div className="flex gap-6">
                                                {['Madame', 'Mademoiselle', 'Monsieur'].map((opt) => (
                                                    <label key={opt} className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="civilite"
                                                            value={opt}
                                                            checked={formData.civilite === opt}
                                                            onChange={handleInputChange}
                                                            className="text-primary focus:ring-primary/20 accent-primary w-4 h-4"
                                                        />
                                                        <span className="text-gray-700 text-sm">{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {errors.civilite && <p className="text-xs text-red-500 mt-1">{errors.civilite}</p>}
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Nom</label>
                                                <input
                                                    type="text"
                                                    name="nom"
                                                    value={formData.nom}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.nom ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Votre nom"
                                                />
                                                {errors.nom && <p className="text-xs text-red-500">{errors.nom}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Prénom</label>
                                                <input
                                                    type="text"
                                                    name="prenom"
                                                    value={formData.prenom}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.prenom ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Votre prénom"
                                                />
                                                {errors.prenom && <p className="text-xs text-red-500">{errors.prenom}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Date de naissance</label>
                                                <input
                                                    type="date"
                                                    name="dateNaissance"
                                                    value={formData.dateNaissance}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.dateNaissance ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                />
                                                {errors.dateNaissance && <p className="text-xs text-red-500">{errors.dateNaissance}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Lieu de naissance</label>
                                                <input
                                                    type="text"
                                                    name="lieuNaissance"
                                                    value={formData.lieuNaissance}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.lieuNaissance ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Lieu de naissance"
                                                />
                                                {errors.lieuNaissance && <p className="text-xs text-red-500">{errors.lieuNaissance}</p>}
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <label className="text-sm font-medium text-gray-700">Adresse personnelle</label>
                                                <input
                                                    type="text"
                                                    name="adressePersonnelle"
                                                    value={formData.adressePersonnelle}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.adressePersonnelle ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Votre adresse personnelle"
                                                />
                                                {errors.adressePersonnelle && <p className="text-xs text-red-500">{errors.adressePersonnelle}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Email de contact</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.email ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="exemple@email.com"
                                                />
                                                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Téléphone</label>
                                                <input
                                                    type="tel"
                                                    name="telephone"
                                                    value={formData.telephone}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.telephone ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="05 55 ..."
                                                />
                                                {errors.telephone && <p className="text-xs text-red-500">{errors.telephone}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Professional Info */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                                            <Briefcase className="w-5 h-5 mr-2 text-primary" />
                                            Informations Professionnelles
                                        </h3>
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Fonction / Titre</label>
                                                <input
                                                    type="text"
                                                    name="fonctionTitre"
                                                    value={formData.fonctionTitre}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.fonctionTitre ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Votre fonction"
                                                />
                                                {errors.fonctionTitre && <p className="text-xs text-red-500">{errors.fonctionTitre}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Établissement / Structure</label>
                                                <input
                                                    type="text"
                                                    name="etablissementStructure"
                                                    value={formData.etablissementStructure}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.etablissementStructure ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Nom de l'établissement"
                                                />
                                                {errors.etablissementStructure && <p className="text-xs text-red-500">{errors.etablissementStructure}</p>}
                                            </div>
                                            
                                            <div className="space-y-2 md:col-span-2">
                                                <label className="text-sm font-medium text-gray-700">Secteur</label>
                                                <div className="flex flex-col gap-3 mt-2">
                                                    {['Industrie pharmaceutique', 'Université / Recherche', 'Autres'].map((opt) => (
                                                        <div key={opt} className="flex items-center gap-3">
                                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                    type="radio"
                                                                    name="secteur"
                                                                    value={opt}
                                                                    checked={formData.secteur === opt}
                                                                    onChange={handleInputChange}
                                                                    className="text-primary focus:ring-primary/20 accent-primary w-4 h-4"
                                                                />
                                                                <span className="text-gray-700 text-sm">{opt}</span>
                                                            </label>
                                                            {opt === 'Autres' && formData.secteur === 'Autres' && (
                                                                <input
                                                                    type="text"
                                                                    name="autreSecteur"
                                                                    value={formData.autreSecteur}
                                                                    onChange={handleInputChange}
                                                                    className={`flex-1 px-3 py-1 text-sm rounded-lg border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.autreSecteur ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                                    placeholder="Précisez..."
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                {errors.secteur && <p className="text-xs text-red-500">{errors.secteur}</p>}
                                                {errors.autreSecteur && formData.secteur === 'Autres' && <p className="text-xs text-red-500">{errors.autreSecteur}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Wilaya</label>
                                                <select
                                                    name="wilaya"
                                                    value={formData.wilaya}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none ${errors.wilaya ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                >
                                                    <option value="">Sélectionnez une wilaya</option>
                                                    {wilayas.map((wilaya) => (
                                                        <option key={wilaya.code} value={wilaya.code}>
                                                            {wilaya.code} - {wilaya.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.wilaya && <p className="text-xs text-red-500">{errors.wilaya}</p>}
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <label className="text-sm font-medium text-gray-700">Adresse professionnelle</label>
                                                <input
                                                    type="text"
                                                    name="adresse"
                                                    value={formData.adresse}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-3 rounded-lg bg-white border focus:ring-2 focus:ring-primary/20 transition-all outline-none ${errors.adresse ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}
                                                    placeholder="Adresse complète"
                                                />
                                                {errors.adresse && <p className="text-xs text-red-500">{errors.adresse}</p>}
                                            </div>
                                        </div>
                                    </div>


                                    {(errors as any)._form && (
                                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                                            <span className="mt-0.5">⚠️</span>
                                            <span>{(errors as any)._form}</span>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-gradient-to-r from-primary to-primary-light text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-not-allowed"
                                    >
                                        <span>{isSubmitting ? 'Soumission en cours...' : 'Soumettre ma demande'}</span>
                                        {!isSubmitting && <Send className="w-5 h-5" />}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </motion.section>
            </div>
        </div>
    );
};

export default Adhesion;
